import { useCallback } from 'react';
import {
  PlaceholderCreationModal,
  PlaceholderConversionModal,
  SemesterSelectionModal,
  RequirementChangeModal,
  SemesterManagementModal,
  HierarchicalProgramModal,
  AddOtherCreditModal,
  AddCourseModal,
  EnrollmentSettingsModal,
} from '../components/shared/modals';
import { calculateTotalCredits } from '../utils/courseUtils';
import { useToast } from '../hooks/useToast';
import type {
  ModalVisibility,
  AllModalData,
  ModalOps,
} from '../hooks/useModals';
import type {
  PlanOperations,
  PlanUtils,
  CourseRequirementsMap,
} from '../hooks/useAcademicPlan';
import type { CourseOps, CourseSearchResult } from '../hooks/useCourseData';
import type { CourseInput } from '../hooks/academicPlan/useCourseOperations';
import { typedApi } from '../services/typedApiClient';
import type { AddCoursesItem, AddCoursesResult, GenEdPreviewCourse } from '../services/typedApiClient';
import type { TimelineResult } from '../hooks/useAppModals';
import type { AcademicPlan } from '../types';
import type { DbUser } from '../contexts/AppContext';

/**
 * Renders the application's modal stack and wires each modal to its
 * relevant slice of plan/course/modal state. Owns no internal state of
 * its own; all open/close logic flows through `modals`, `modalOps`, and
 * the per-modal isOpen/on*Close props.
 */
export interface ModalManagerProps {
  modals: ModalVisibility;
  modalData: AllModalData;
  modalOps: ModalOps;
  planOps: PlanOperations;
  planUtils: PlanUtils;
  courses: CourseSearchResult[];
  courseOps: CourseOps;
  requirements: CourseRequirementsMap;
  currentPlanId: number | null;
  showSemesterModal: boolean;
  setShowSemesterModal: (show: boolean) => void;
  academicPlan: AcademicPlan | null | undefined;
  onProgramsUpdated: () => Promise<void> | void;
  onOpenAPCredits: () => void;
  onAddOtherCredit: () => void;
  onOpenBookmarkedCourses: () => void;
  onSetShowBookmarkedCourses: (visible: boolean) => void;
  showBookmarkedCourses: boolean;

  // Other Credit Modal
  isOtherCreditModalOpen: boolean;
  onCloseOtherCredit: () => void;
  onOtherCreditSubmit: (creditData: Record<string, unknown>) => Promise<void> | void;

  // Add Course Modal
  isAddCourseModalOpen: boolean;
  onCloseAddCourse: () => void;
  onAddCourseFromModal: (course: CourseInput, semesterCode: string) => Promise<void> | void;
  onAddCoursesFromModal: (items: AddCoursesItem[], semesterCode: string) => Promise<AddCoursesResult | null>;
  addCourseTargetSemester: { code: string; name: string } | null;

  // Enrollment Settings Modal
  isTimelineModalOpen: boolean;
  onCloseTimeline: () => void;
  dbUser: DbUser | null;
  onTimelineSave: (result: TimelineResult) => Promise<void> | void;
  onResetPlan: () => Promise<void> | void;
}

export function ModalManager({
  modals,
  modalData,
  modalOps,
  planOps,
  planUtils,
  courses,
  courseOps,
  requirements,
  currentPlanId,
  showSemesterModal,
  setShowSemesterModal,
  academicPlan,
  onProgramsUpdated,
  onOpenAPCredits,
  onAddOtherCredit,
  onOpenBookmarkedCourses,
  onSetShowBookmarkedCourses,
  showBookmarkedCourses,
  isOtherCreditModalOpen,
  onCloseOtherCredit,
  onOtherCreditSubmit,
  isAddCourseModalOpen,
  onCloseAddCourse,
  onAddCourseFromModal,
  onAddCoursesFromModal,
  addCourseTargetSemester,
  isTimelineModalOpen,
  onCloseTimeline,
  dbUser,
  onTimelineSave,
  onResetPlan,
}: ModalManagerProps) {
  const { showError } = useToast();
  // Gen-ed OR defaults for the add-course modal's selection (read-only).
  const previewGenEd = useCallback(async (courseCodes: string[]): Promise<GenEdPreviewCourse[]> => {
    if (currentPlanId == null) return [];
    const response = await typedApi.academicPlans.previewGenEd(currentPlanId, courseCodes);
    return response.courses ?? [];
  }, [currentPlanId]);
  const apCredits = academicPlan?.semesters?.['000008'] || [];
  const otherCredits = academicPlan?.semesters?.['000009'] || [];
  const apCreditsCount = apCredits.length;
  const apTotalCredits = calculateTotalCredits(apCredits);
  const otherCreditsCount = otherCredits.length;
  const otherTotalCredits = calculateTotalCredits(otherCredits);

  const bookmarkedCourses = academicPlan?.bookmarkedCourses || [];
  const bookmarkedCoursesCount = bookmarkedCourses.length;
  const bookmarkedTotalCredits = calculateTotalCredits(bookmarkedCourses);

  // If the key exists in the semesters object, the credit type is selected.
  const apCreditsSelected = !!academicPlan?.semesters && '000008' in academicPlan.semesters;
  const otherCreditsSelected = !!academicPlan?.semesters && '000009' in academicPlan.semesters;

  return (
    <>
      <PlaceholderCreationModal
        isOpen={modals.placeholder}
        onClose={modalOps.closePlaceholderModal}
        onSubmit={(placeholderData, targetSemester) => {
          planOps.createPlaceholder(
            placeholderData as Parameters<PlanOperations['createPlaceholder']>[0],
            targetSemester || modalData.placeholderTargetSemester
          );
          modalOps.closePlaceholderModal();
        }}
        editingPlaceholder={modalData.editingPlaceholder as Record<string, unknown> | null}
        targetSemester={modalData.placeholderTargetSemester}
      />

      <PlaceholderConversionModal
        isOpen={modals.conversion}
        onClose={modalOps.closeConversionModal}
        placeholderData={modalData.placeholderToConvert as never}
        canFulfillPlaceholder={planUtils.canFulfillPlaceholder as never}
        courses={courses}
        onSearch={courseOps.searchCourses}
        onConvert={(placeholderCode, realCourse, fromSemester, fromIndex) => {
          // convertPlaceholder rethrows persistence failures (#205) — catch
          // here or a network error becomes an unhandled rejection with no
          // user feedback after the modal has already closed. Promise.resolve
          // tolerates non-promise doubles (jest resetMocks) defensively.
          Promise.resolve(
            planOps.convertPlaceholder(
              placeholderCode,
              realCourse as Parameters<PlanOperations['convertPlaceholder']>[1],
              fromSemester,
              fromIndex
            )
          ).catch((err: unknown) => {
            showError(
              `Failed to convert placeholder: ${err instanceof Error ? err.message : String(err)}`
            );
          });
          modalOps.closeConversionModal();
        }}
      />

      <SemesterSelectionModal
        isOpen={modals.semesterSelection}
        onClose={modalOps.closeSemesterSelection}
        onSelect={(semester: string) => {
          if (modalData.selectedCourseForSemester) {
            const course = modalData.selectedCourseForSemester;
            const context = modalData.moveContext;

            if (semester === 'bookmarks') {
              planOps.toggleBookmark(course);
              if (context?.fromSemester && context.fromSemester !== 'bookmarks') {
                planOps.removeCourse(context.fromSemester, context.index);
              }
            } else {
              if (context?.fromSemester && context.fromSemester !== 'bookmarks') {
                planOps.moveCourse(course, context.fromSemester, semester, context.index);
              } else {
                planOps.addCourse(course, semester);
                if (context?.fromSemester === 'bookmarks' && course.code) {
                  planOps.removeBookmarkByCode(course.code);
                }
              }
            }
            modalOps.closeSemesterSelection();
          }
        }}
        excludeSemesters={modalData.moveContext?.fromSemester ? [modalData.moveContext.fromSemester] : []}
      />

      <RequirementChangeModal
        isOpen={modals.requirement}
        onClose={modalOps.closeRequirementModal}
        course={modalData.requirementModalData?.course}
        currentRequirement={modalData.requirementModalData?.currentRequirements}
        requirements={requirements}
        onSave={(course, requirements) => {
          if (course.code) {
            planOps.updateCourseRequirements(course.code, requirements);
          }
          modalOps.closeRequirementModal();
        }}
      />

      <SemesterManagementModal
        isOpen={showSemesterModal}
        onClose={() => setShowSemesterModal(false)}
        planId={currentPlanId}
        onSave={async () => {
          setShowSemesterModal(false);
          if (planOps && planOps.refreshPlan) {
            await planOps.refreshPlan();
          } else {
            console.error('refreshPlan method not available');
            window.location.reload();
          }
        }}
        apCreditsCount={apCreditsCount}
        apTotalCredits={apTotalCredits}
        otherCreditsCount={otherCreditsCount}
        otherTotalCredits={otherTotalCredits}
        onOpenAPCredits={onOpenAPCredits}
        onAddOtherCredit={onAddOtherCredit}
        bookmarkedCoursesCount={bookmarkedCoursesCount}
        bookmarkedTotalCredits={bookmarkedTotalCredits}
        onOpenBookmarkedCourses={onOpenBookmarkedCourses}
        onSetShowBookmarkedCourses={onSetShowBookmarkedCourses}
        initialAPCreditsSelected={apCreditsSelected}
        initialOtherCreditsSelected={otherCreditsSelected}
        initialBookmarkedCoursesVisible={showBookmarkedCourses}
      />

      <HierarchicalProgramModal
        isOpen={modals.programSelection}
        onClose={modalOps.closeProgramSelection}
        hierarchicalData={modalData.programSelectionData}
        academicPlan={academicPlan}
        onProgramsUpdated={onProgramsUpdated}
      />

      <AddOtherCreditModal
        isOpen={isOtherCreditModalOpen}
        onClose={onCloseOtherCredit}
        onSubmit={onOtherCreditSubmit}
      />

      <AddCourseModal
        isOpen={isAddCourseModalOpen}
        onClose={onCloseAddCourse}
        onAddCourse={onAddCourseFromModal}
        onAddCourses={onAddCoursesFromModal}
        onPreviewGenEd={previewGenEd}
        onAddToBookmarks={planOps.addToBookmarks}
        targetSemester={addCourseTargetSemester}
        academicPlan={academicPlan}
      />

      <EnrollmentSettingsModal
        isOpen={isTimelineModalOpen}
        onClose={onCloseTimeline}
        currentEnrollment={dbUser?.enrollment_start_semester}
        expectedGraduation={dbUser?.expected_graduation_semester}
        userId={dbUser?.id}
        onSave={onTimelineSave}
        onResetPlan={onResetPlan}
      />
    </>
  );
}
